import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import { StimulusResourceType, Subject, Years } from 'global/constants';
import { InquisitiveStimulusResourceType } from 'global/graphql/scalars.type';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { getNewFilterSelection } from 'approot/shared/filters/filter-option/filter-option.utils';
import DropdownMultiselectOptions from 'approot/shared/dropdown/dropdown-multiselect';
import Bouncer from 'approot/shared/bouncer/bouncer';
import Pagination from 'approot/shared/pagination/pagination';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import { PStyled } from 'approot/shared/styles/typography.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { DROPDOWN_OPTIONS_MEDIUM } from 'approot/shared/dropdown/dropdown.constants';
import type {
  SearchStimulusResourcesQuery,
  SearchStimulusResourcesQueryVariables,
} from '../stimulus.graphql.generated';
import { SEARCH_STIMULUS_RESOURCES } from '../stimulus.graphql';
import * as Styled from './stimulus-search.style';
import StimulusSearchItem from './stimulus-search-item';
import StimulusSearchItemPreview from './stimulus-search-item-preview';
import { getStimulusFilter } from './stimulus-search.utils';
import searchEmpty from './stimulus-search-empty.svg';
import {
  getAllYearsFilterOption,
  useYearTranslation,
} from 'approot/shared/logic/years';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { spacing24, spacing8 } from 'approot/shared/styles/spacing.style';
import { ALL_SUBJECTS_OPTION } from 'webclient.constants';
import type { GetStimulusLessonQuery } from '../../../create-stimulus/create-stimulus.graphql.generated';
import { Box } from 'approot/shared/design-system/box/box.style';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  type?: InquisitiveStimulusResourceType;
  withMediaPreview?: boolean;
  selectedStimulus?: Pluck<
    SearchStimulusResourcesQuery,
    'searchStimulusResources.stimulusResources'
  >;
  scrollableContainerId?: string;
  stackFilters?: boolean;
  subjects?: Pluck<GetStimulusLessonQuery, 'getLessonById.subjects'>[];
  years?: Pluck<GetStimulusLessonQuery, 'getLessonById.years'>[];
  onStimulusSelected: (
    stimulus: Pluck<
      SearchStimulusResourcesQuery,
      'searchStimulusResources.stimulusResources'
    >
  ) => void;
};

const StimulusSearch = ({
  type,
  withMediaPreview,
  selectedStimulus,
  scrollableContainerId,
  stackFilters,
  subjects: lessonSubjects,
  years: lessonYears,
  onStimulusSelected,
}: Props) => {
  const { t } = useTranslation();
  const { getYearLabel } = useYearTranslation();

  const subjectFilterDefaults = useMemo(
    () => lessonSubjects?.map(s => s.name),
    [lessonSubjects]
  );
  const yearFilterDefaults = useMemo(
    () => lessonYears?.map(y => y.name),
    [lessonYears]
  );

  const containerRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [types, setTypes] = useState<InquisitiveStimulusResourceType[]>(
    type ? [type] : []
  );
  const [subjects, setSubjects] = useState<Subject[]>(
    subjectFilterDefaults || []
  );
  const [years, setYears] = useState<Years[]>(yearFilterDefaults || []);
  const [page, setPage] = useState<number>(0);
  const [text, setText] = useState<string>('');

  const {
    error: startUpDataError,
    loading: startUpDataLoading,
    yearOptions,
    subjectOptions,
  } = useGetStartupData();

  const yearOptionsWithAll = useMemo(
    () => (yearOptions ? [...yearOptions, getAllYearsFilterOption(t)] : []),
    [yearOptions, t]
  );
  const subjectOptionsWithAll = useMemo(
    () => (subjectOptions ? [...subjectOptions, ALL_SUBJECTS_OPTION] : []),
    [subjectOptions]
  );

  const [search, { data, loading, error }] = useLoggingLazyQuery<
    SearchStimulusResourcesQuery,
    SearchStimulusResourcesQueryVariables
  >(SEARCH_STIMULUS_RESOURCES, {
    // set to no-cache because it is shared with Stimulus browser
    // which use lazy loading. Might be better this always gets the
    // latest data anyway
    fetchPolicy: 'no-cache',
  });

  const searchData = data?.searchStimulusResources;
  const totalPageCount = searchData
    ? Math.ceil(searchData.total / searchData.pageSize)
    : 0;
  const contentTypeLabels = useMemo(() => getStimulusFilter(), []);
  const resultLength = searchData?.stimulusResources.length || 0;

  const resetSearch = useMemo(
    () =>
      debounce(
        (
          text: string,
          subjects: Subject[],
          years: Years[],
          types: InquisitiveStimulusResourceType[],
          page: number
        ) => {
          setPage(page);
          search({
            variables: {
              input: {
                text,
                subjects,
                years,
                types: type ? [type] : types,
                page,
                includeNonPublished: true,
              },
            },
          });
          if (containerRef.current) {
            containerRef.current.scrollTo(0, 0);
          } else {
            window.scrollTo(0, 0);
          }
        },
        300
      ),
    [type, setPage, search]
  );

  const handleChangeType = useCallback(
    (value: StimulusResourceType) => {
      const newType = getNewFilterSelection(types, value);
      setTypes(newType);
      resetSearch(text, subjects, years, newType, 0);
    },
    [text, subjects, years, types, setTypes, resetSearch]
  );

  const handleChangeYear = useCallback(
    (value: Years) => {
      const newYear = getNewFilterSelection(years, value);
      setYears(newYear);
      resetSearch(text, subjects, newYear, types, 0);
    },
    [text, subjects, years, types, setYears, resetSearch]
  );

  const handleChangeSubject = useCallback(
    (value: Subject) => {
      const newSubjects = getNewFilterSelection(subjects, value);
      setSubjects(newSubjects);
      resetSearch(text, newSubjects, years, types, 0);
    },
    [text, subjects, years, types, setSubjects, resetSearch]
  );

  const handleChangePage = useCallback(
    (currentPage: number) => {
      setPage(currentPage);
      resetSearch(text, subjects, years, types, currentPage);
    },
    [text, setPage, subjects, years, types, resetSearch]
  );

  useEffect(() => {
    if (scrollableContainerId) {
      containerRef.current = document.getElementById(scrollableContainerId);
    }
    resetSearch(text, subjects, years, types, page);
    // eslint-disable-next-line
  }, [resetSearch]);

  return (
    <>
      <ScreenReaderOnly
        id="stimulus-search-live"
        role="region"
        aria-live="polite"
      >
        {loading && 'Loading stimulus content'}
        {resultLength > 0 &&
          `Displaying ${resultLength} of ${
            data?.searchStimulusResources.total
          } ${pluralize('result', resultLength)}`}
      </ScreenReaderOnly>
      <Styled.StimulusSearchField
        data-selector="stimulus-search"
        id="stimulusSearch"
        name="stimulusSearch"
        ref={searchRef}
        onKeyDown={e => {
          if (e.key === 'Enter' && searchRef.current) {
            const text = searchRef.current.value.trim();
            setText(text);
            resetSearch(text, subjects, years, types, 0);
          }
        }}
      />
      {startUpDataError ? (
        <PStyled marginBottom={spacing24} marginTop={spacing8} textAlignCenter>
          We&rsquo;re having trouble loading our filters. Try refreshing the
          page
        </PStyled>
      ) : (
        <Styled.StimulusFilters $isStacked={stackFilters}>
          {!type && (
            <Styled.StimulusFilterGroup>
              <DropdownMultiselectOptions
                label="Stimulus Type"
                labelUnselected="Types"
                options={contentTypeLabels}
                isLoading={startUpDataLoading}
                selectedDropdownOptions={types}
                dataSelector="stimulus-type"
                menuWidth={DROPDOWN_OPTIONS_MEDIUM}
                menuMatchToggleWidth={false}
                handleChangeCheckbox={handleChangeType}
              />
            </Styled.StimulusFilterGroup>
          )}
          <Styled.StimulusFilterGroup>
            <DropdownMultiselectOptions
              label="Subject"
              labelUnselected="Subjects"
              options={subjectOptionsWithAll}
              isLoading={startUpDataLoading}
              selectedDropdownOptions={subjects}
              dataSelector="stimulus-subject"
              menuWidth={DROPDOWN_OPTIONS_MEDIUM}
              menuMatchToggleWidth={false}
              handleChangeCheckbox={handleChangeSubject}
            />
          </Styled.StimulusFilterGroup>
          <Styled.StimulusFilterGroup>
            <DropdownMultiselectOptions
              label={getYearLabel()}
              labelUnselected={getYearLabel({ plural: true })}
              options={yearOptionsWithAll}
              tFunction={t}
              isLoading={startUpDataLoading}
              selectedDropdownOptions={years}
              dataSelector="stimulus-year"
              menuWidth={DROPDOWN_OPTIONS_MEDIUM}
              menuMatchToggleWidth={false}
              handleChangeCheckbox={handleChangeYear}
            />
          </Styled.StimulusFilterGroup>
        </Styled.StimulusFilters>
      )}

      {error ? (
        <ErrorMessageActionable ariaPage="Stimulus search" paddingTop={0} />
      ) : loading ? (
        <Bouncer includeScreenReaderProps={false} />
      ) : data?.searchStimulusResources.stimulusResources.length === 0 ? (
        <Styled.StimulusSearchEmpty>
          <img src={searchEmpty} alt="Sad face" aria-hidden />
          <Styled.StimulusSearchEmptyTitle data-selector="empty-stimulus-search">
            Oh no! We couldn&rsquo;t find any matching results
          </Styled.StimulusSearchEmptyTitle>
          <PStyled>
            Try searching for a different term or different filters.{' '}
          </PStyled>
        </Styled.StimulusSearchEmpty>
      ) : data ? (
        <>
          <Box $marginBottom={32}>
            {data?.searchStimulusResources.stimulusResources.map(s =>
              !withMediaPreview ? (
                <StimulusSearchItem
                  key={s.id}
                  stimulus={s}
                  action={
                    <SecondaryButton
                      type="button"
                      $isSmall
                      aria-label={`Choose ${s.name}`}
                      onClick={() => onStimulusSelected(s)}
                    >
                      Choose
                    </SecondaryButton>
                  }
                />
              ) : (
                <StimulusSearchItemPreview
                  key={s.id}
                  stimulus={s}
                  type={type}
                  isActive={s.id === selectedStimulus?.id}
                  onClick={() => onStimulusSelected(s)}
                />
              )
            )}
          </Box>
          <Pagination
            currentPage={page}
            totalPageCount={totalPageCount}
            aria-label="Stimulus search pagination"
            onNext={() => handleChangePage(page + 1)}
            onPrevious={() => handleChangePage(page - 1)}
          />
        </>
      ) : null}
    </>
  );
};

export default StimulusSearch;
